// Shared helper to compute casting progress with per-part clamping.
// Returns totals (required, decided, fulfilled) and a 0-100 percent value.

const defaultRequiredKeys = ['required', 'required_number', 'requiredCount', 'number'];
const defaultAssignedKeys = ['decided', 'signed_count', 'decidedCount', 'assigned', 'assignedCount'];

const pickNumericValue = (row, keys = []) => {
  if (!row) return 0;
  for (const key of keys) {
    if (!key) continue;
    const raw = row[key];
    if (raw == null) continue;
    const num = Number(raw);
    if (!Number.isNaN(num)) return num;
  }
  return 0;
};

export function calculateCastingProgress(parts = [], options = {}) {
  const getRequired =
    typeof options.getRequired === 'function'
      ? options.getRequired
      : (part) =>
          pickNumericValue(part, [
            options.requiredKey,
            ...(options.requiredKeys || []),
            ...defaultRequiredKeys,
          ]);

  const getAssigned =
    typeof options.getAssigned === 'function'
      ? options.getAssigned
      : (part) =>
          pickNumericValue(part, [
            options.assignedKey,
            ...(options.assignedKeys || []),
            ...defaultAssignedKeys,
          ]);

  let requiredTotal = 0;
  let assignedTotal = 0;
  let fulfilledTotal = 0;

  (Array.isArray(parts) ? parts : []).forEach((part) => {
    const required = Math.max(0, Math.floor(getRequired(part) || 0));
    const assigned = Math.max(0, Math.floor(getAssigned(part) || 0));
    if (required > 0) {
      requiredTotal += required;
      fulfilledTotal += Math.min(required, assigned);
    } else if (assigned > 0) {
      // part without requirement still counts toward decided, but not fulfillment
      assignedTotal += assigned;
      return;
    }
    assignedTotal += assigned;
  });

  const percent =
    requiredTotal > 0
      ? Math.min(100, Math.round((fulfilledTotal / requiredTotal) * 100))
      : 0;

  return {
    required: requiredTotal,
    decided: assignedTotal,
    fulfilled: fulfilledTotal,
    percent,
  };
}

export function normalizeCastingProgressSummary(summary) {
  if (!summary || typeof summary !== 'object') {
    return {
      mode: 'part_based',
      targetCount: null,
      participantCount: 0,
      required: 0,
      decided: 0,
      fulfilled: 0,
      percent: 0,
    };
  }
  const mode = ['part_based', 'headcount', 'not_required'].includes(summary.mode)
    ? summary.mode
    : 'part_based';
  const required = Math.max(0, Number(summary.required) || 0);
  const decided = Math.max(0, Number(summary.decided) || 0);
  const fulfilled = Math.max(0, Number(summary.fulfilled) || 0);
  const participantCount = Math.max(0, Number(summary.participantCount) || 0);
  const targetCount = mode === 'headcount'
    ? Math.max(1, Number(summary.targetCount) || required || 1)
    : null;
  return {
    mode,
    targetCount,
    participantCount,
    required,
    decided,
    fulfilled,
    percent: mode === 'not_required'
      ? 100
      : Math.max(0, Math.min(100, Math.round(Number(summary.percent) || 0))),
  };
}

export function formatCastingProgressNote(summary) {
  const normalized = normalizeCastingProgressSummary(summary);
  if (normalized.mode === 'not_required') return '管理不要';
  if (normalized.mode === 'headcount') {
    return `${normalized.participantCount}/${normalized.targetCount}人`;
  }
  return normalized.required > 0
    ? `${normalized.fulfilled}/${normalized.required}`
    : '0/0';
}
